<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import TicketPriorityBadge from '../../shared/TicketPriorityBadge.vue';

const SEARCH_MIN_CHARS = 4;
const SEARCH_DEBOUNCE_MS = 350;

const props = defineProps({
    tickets: { type: Array, default: () => [] },
    activeTicketId: { type: [String, Number], default: null },
    labels: { type: Object, default: () => ({}) },
    channelName: { type: String, default: '' },
    total: { type: Number, default: null },
    hasMore: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
});

const emit = defineEmits(['select-ticket', 'search', 'load-more']);

const localQuery = ref('');
const queryDebounceTimer = ref(null);
const listRoot = ref(null);
const loadSentinel = ref(null);
let intersectionObserver = null;

const badgeCount = computed(() => {
    if (props.total !== null && props.total !== undefined) {
        return props.total;
    }
    return props.tickets.length;
});

const isActive = (ticket) => String(ticket.id) === String(props.activeTicketId);

const isSeenRow = (ticket) => ticket.status === 'in_progress';

function commitQuery() {
    const trimmed = (localQuery.value || '').trim();
    let next = '';
    if (trimmed.length === 0) {
        next = '';
    } else if (trimmed.length >= SEARCH_MIN_CHARS) {
        next = trimmed;
    }
    emit('search', next);
}

function onQueryInput(event) {
    localQuery.value = event.target.value;
    if (queryDebounceTimer.value) {
        clearTimeout(queryDebounceTimer.value);
    }
    queryDebounceTimer.value = setTimeout(() => {
        commitQuery();
    }, SEARCH_DEBOUNCE_MS);
}

function setupInfiniteScroll() {
    if (intersectionObserver) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
    }
    const root = listRoot.value;
    const sentinel = loadSentinel.value;
    if (!root || !sentinel) {
        return;
    }
    intersectionObserver = new IntersectionObserver(
        (entries) => {
            const entry = entries[0];
            if (entry?.isIntersecting && props.hasMore && !props.loading) {
                emit('load-more');
            }
        },
        { root, rootMargin: '0px 0px 120px 0px', threshold: 0 },
    );
    intersectionObserver.observe(sentinel);
}

onMounted(() => {
    setupInfiniteScroll();
});

onBeforeUnmount(() => {
    if (queryDebounceTimer.value) {
        clearTimeout(queryDebounceTimer.value);
    }
    if (intersectionObserver) {
        intersectionObserver.disconnect();
    }
});

watch(
    () => [props.hasMore, props.loading, props.tickets.length],
    () => {
        setupInfiniteScroll();
    },
    { flush: 'post' },
);
</script>

<template>
    <aside
        id="ticket-desk-ticket-list"
        ref="listRoot"
        class="ticket-desk-list"
        data-testid="ticket-desk-ticket-list"
    >
        <div v-if="channelName" class="ticket-desk-list__channel" data-testid="ticket-desk-list-channel">
            {{ channelName }}
        </div>
        <div class="ticket-desk-list__search">
            <span class="ticket-desk-icon ticket-desk-list__search-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
            </span>
            <input
                :value="localQuery"
                type="search"
                placeholder="Cerca id, titolo o email (min. 4 caratteri)..."
                :aria-label="labels.search || 'Cerca ticket'"
                @input="onQueryInput"
            />
        </div>
        <div class="ticket-desk-list__heading">
            <h2>{{ labels.myTickets || labels.my_tickets || 'I miei ticket' }}</h2>
            <span class="ticket-desk-badge ticket-desk-badge--neutral">{{ badgeCount }}</span>
        </div>
        <div v-if="tickets.length" class="ticket-desk-list__rows">
            <button
                v-for="ticket in tickets"
                :key="ticket.id"
                type="button"
                class="ticket-desk-list__row"
                :class="{
                    'ticket-desk-list__row--active': isActive(ticket),
                    'ticket-desk-list__row--seen': isSeenRow(ticket),
                }"
                @click="emit('select-ticket', ticket)"
            >
                <span class="ticket-desk-list__meta">
                    <span class="ticket-desk-list__id">#{{ ticket.id }}</span>
                    <TicketPriorityBadge
                        :priority="ticket.priority"
                        :priority-label="ticket.priority_label"
                        size="sm"
                        test-id="ticket-desk-list-priority"
                    />
                </span>
                <span class="ticket-desk-list__title">{{ ticket.title }}</span>
                <span class="ticket-desk-list__subtitle">
                    {{ ticket.customer_name || ticket.customer_email || ticket.customer }}
                    <template v-if="ticket.status_label || ticket.status"> · {{ ticket.status_label || ticket.status }}</template>
                    <template v-if="ticket.time || ticket.updated_at_label || ticket.created_at_label"> · {{ ticket.time || ticket.updated_at_label || ticket.created_at_label }}</template>
                </span>
            </button>
            <div ref="loadSentinel" class="ticket-desk-list__sentinel" aria-hidden="true" />
            <p v-if="loading" class="ticket-desk-list__loading">{{ labels.loading || 'Caricamento…' }}</p>
        </div>
        <p v-else-if="loading" class="ticket-desk-empty">{{ labels.loading || 'Caricamento…' }}</p>
        <p v-else class="ticket-desk-empty">{{ labels.noTickets || 'Nessun ticket trovato' }}</p>
    </aside>
</template>
